import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type RefObject,
} from 'react';
import { api } from '../lib/api.ts';
import { Icon, type IconName } from '../lib/icons.tsx';
import {
  MAX_HIGHLIGHT_BYTES,
  highlightLines,
  highlightLinesSync,
  languageForPath,
  languageLabel,
  loadLanguage,
  plainLines,
  type CodeToken,
  type LangId,
} from '../lib/highlight.ts';
import type { DirListing, FileContent, FileEntry } from '../lib/protocol.ts';

/**
 * Browser for a project working tree: one directory at a time, with the head
 * of any file, and an editor for text the phone can hold in full.
 * Everything comes from the gateway, so the phone sees the same tree the
 * agents run in.
 */
export function FilesView({
  projectId,
  host,
  hidden,
}: {
  projectId: string;
  host?: string;
  hidden?: boolean;
}): JSX.Element {
  const [dir, setDir] = useState('');
  const [listing, setListing] = useState<DirListing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [filePath, setFilePath] = useState<string | null>(null);
  const [file, setFile] = useState<FileContent | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setDir('');
    setListing(null);
    setFilter('');
    setFilePath(null);
    setFile(null);
    setFileError(null);
  }, [projectId, host]);

  useEffect(() => {
    if (hidden || !projectId) return;
    let cancelled = false;
    setBusy(true);
    void api
      .files(projectId, dir)
      .then((next) => {
        if (cancelled) return;
        setListing(next);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, dir, hidden, nonce, host]);

  const loadFile = useCallback(
    async (path: string): Promise<void> => {
      setFilePath(path);
      setFile(null);
      setFileError(null);
      setFileBusy(true);
      try {
        setFile(await api.fileContent(projectId, path));
      } catch (err) {
        setFileError(err instanceof Error ? err.message : String(err));
      } finally {
        setFileBusy(false);
      }
    },
    [projectId],
  );

  /** Drop the old rows so a tap can never land on the previous directory. */
  const goDir = (path: string): void => {
    setFilePath(null);
    setFile(null);
    setFileError(null);
    setListing(null);
    setFilter('');
    setDir(path);
  };

  const open = (entry: FileEntry): void => {
    if (entry.kind === 'dir') goDir(entry.path);
    else if (entry.kind === 'file') void loadFile(entry.path);
  };

  const saveFile = async (text: string): Promise<void> => {
    if (!filePath) return;
    const next = await api.setFileContent(projectId, filePath, text);
    setFile(next);
    setFileError(null);
    setNonce((n) => n + 1);
  };

  const closeFile = (): void => {
    setFilePath(null);
    setFile(null);
    setFileError(null);
  };

  const refresh = (): void => {
    if (filePath) void loadFile(filePath);
    else setNonce((n) => n + 1);
  };

  const entries = listing?.entries ?? [];
  const needle = filter.trim().toLowerCase();
  const shown = needle ? entries.filter((e) => e.name.toLowerCase().includes(needle)) : entries;
  const dirs = shown.filter((e) => e.kind === 'dir').length;

  return (
    <div className="git-pane files-pane" id="pane-files" role="tabpanel" aria-labelledby="tab-files" hidden={hidden}>
      {filePath ? (
        <FileScreen
          path={filePath}
          file={file}
          busy={fileBusy}
          error={fileError}
          onBack={closeFile}
          onRefresh={refresh}
          onSave={saveFile}
        />
      ) : (
        <>
          <div className="files-head">
            <Crumbs path={dir} onGo={goDir} />
            <button
              type="button"
              className="icon-btn files-head-btn"
              onClick={refresh}
              aria-label="Refresh"
              disabled={busy}
            >
              <Icon name="refresh" size={18} strokeWidth={2.2} />
            </button>
          </div>

          <div className="git-body files-body">
            {error && <div className="note note-error">{error}</div>}
            {busy && !listing && (
              <div className="thinking">
                <span className="spinner" /> reading directory
              </div>
            )}
            {listing && (
              <>
                {entries.length > 8 && (
                  <label className="files-filter">
                    <Icon name="search" size={16} strokeWidth={2} className="files-filter-icon" />
                    <input
                      type="text"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder="Filter this folder"
                      aria-label="Filter this folder"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      enterKeyHint="search"
                    />
                    {filter && (
                      <button
                        type="button"
                        className="files-filter-clear"
                        onClick={() => setFilter('')}
                        aria-label="Clear filter"
                      >
                        <Icon name="close" size={16} strokeWidth={2.2} />
                      </button>
                    )}
                  </label>
                )}

                <div className="files-count">{countLine(shown.length, dirs, needle !== '')}</div>

                <div className="files-list">
                  {listing.parent !== null && !needle && (
                    <button
                      type="button"
                      className="files-row files-row-up"
                      onClick={() => goDir(listing.parent ?? '')}
                      aria-label="Parent directory"
                    >
                      <span className="files-badge files-tone-dir">
                        <Icon name="up" size={16} strokeWidth={2.2} />
                      </span>
                      <span className="files-name">..</span>
                    </button>
                  )}
                  {shown.map((entry) => (
                    <EntryRow key={entry.path} entry={entry} onOpen={open} />
                  ))}
                </div>

                {shown.length === 0 && (
                  <div className="git-clean">{needle ? 'Nothing matches' : 'Empty directory'}</div>
                )}
                {listing.truncated && <div className="git-more">and more unshown entries</div>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function EntryRow({ entry, onOpen }: { entry: FileEntry; onOpen: (entry: FileEntry) => void }): JSX.Element {
  const look = lookOf(entry);
  return (
    <button
      type="button"
      className={`files-row${entry.kind === 'other' ? ' files-row-other' : ''}`}
      onClick={() => onOpen(entry)}
      disabled={entry.kind === 'other'}
      aria-label={entry.kind === 'dir' ? `Open folder ${entry.name}` : `View file ${entry.name}`}
    >
      <span className={`files-badge files-tone-${look.tone}`}>
        <Icon name={look.icon} size={16} strokeWidth={2} />
      </span>
      <span className="files-name">{entry.name}</span>
      <span className="files-meta">
        {entry.kind === 'file' && entry.size != null && (
          <span className="files-size">{formatSize(entry.size)}</span>
        )}
        {entry.mtime != null && <span className="files-when">{formatWhen(entry.mtime)}</span>}
      </span>
      {entry.kind === 'dir' && <Icon name="chevronRight" size={16} strokeWidth={2} className="files-chevron" />}
    </button>
  );
}

/** One file: a header that names it, a bar of facts and actions, then the code. */
function FileScreen({
  path,
  file,
  busy,
  error,
  onBack,
  onRefresh,
  onSave,
}: {
  path: string;
  file: FileContent | null;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onRefresh: () => void;
  onSave: (text: string) => Promise<void>;
}): JSX.Element {
  const [wrap, setWrap] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const cut = path.lastIndexOf('/');
  const name = cut === -1 ? path : path.slice(cut + 1);
  const folder = cut === -1 ? '' : path.slice(0, cut);
  const raw = file && !file.binary ? file.text : '';
  // A file that ends in a newline would otherwise draw a phantom last line.
  const text = raw.endsWith('\n') ? raw.slice(0, -1) : raw;
  const lineCount = raw === '' ? 0 : text.split('\n').length;
  const canEdit = Boolean(file && !file.binary && !file.truncated && !busy && !error);

  useEffect(() => {
    setEditing(false);
    setDraft('');
    setSaveError(null);
  }, [path]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (!editing) return;
    editorRef.current?.focus();
  }, [editing]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const startEdit = (): void => {
    if (!canEdit || file == null) return;
    setDraft(file.text);
    setSaveError(null);
    setEditing(true);
  };

  const cancelEdit = (): void => {
    setEditing(false);
    setDraft('');
    setSaveError(null);
  };

  const handleBack = (): void => {
    if (editing) {
      cancelEdit();
      return;
    }
    onBack();
  };

  const saveEdit = async (): Promise<void> => {
    if (saving) return;
    if (file != null && draft === file.text) {
      setEditing(false);
      setSaveError(null);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
      setDraft('');
      setSaveError(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="files-head files-head-file">
        <button
          type="button"
          className="icon-btn files-head-btn"
          onClick={handleBack}
          aria-label={editing ? 'Cancel editing' : 'Back to directory'}
        >
          <Icon name="back" size={20} strokeWidth={2.2} />
        </button>
        <span className="files-title">
          <span className="files-title-name">{name}</span>
          {folder && <span className="files-title-dir">{folder}/</span>}
        </span>
        {editing ? (
          <button
            type="button"
            className="icon-btn files-head-btn files-head-save"
            onClick={() => void saveEdit()}
            aria-label="Save file"
            disabled={saving}
          >
            <Icon name="check" size={18} strokeWidth={2.2} />
          </button>
        ) : (
          <>
            {canEdit && (
              <button
                type="button"
                className="icon-btn files-head-btn"
                onClick={startEdit}
                aria-label="Edit file"
              >
                <Icon name="pencil" size={18} strokeWidth={2.2} />
              </button>
            )}
            <button
              type="button"
              className="icon-btn files-head-btn"
              onClick={onRefresh}
              aria-label="Refresh"
              disabled={busy}
            >
              <Icon name="refresh" size={18} strokeWidth={2.2} />
            </button>
          </>
        )}
      </div>

      {file && !busy && !error && !editing && (
        <div className="files-bar">
          <span className="files-chip">{languageLabel(path)}</span>
          <span className="files-bar-meta">
            {formatSize(file.size)}
            {lineCount > 0 && ` · ${lineCount.toLocaleString()} lines`}
          </span>
          {file.truncated && <span className="files-chip files-chip-warn">head only</span>}
          <span className="files-bar-gap" />
          {!file.binary && raw !== '' && (
            <>
              <button
                type="button"
                className={`files-bar-btn${wrap ? ' files-bar-btn-on' : ''}`}
                onClick={() => setWrap((w) => !w)}
                aria-pressed={wrap}
                aria-label="Wrap long lines"
              >
                <Icon name="wrap" size={16} strokeWidth={2} />
              </button>
              <button
                type="button"
                className={`files-bar-btn${copied ? ' files-bar-btn-on' : ''}`}
                onClick={() => void copy()}
                aria-label={copied ? 'Copied' : 'Copy file'}
              >
                <Icon name={copied ? 'check' : 'copy'} size={16} strokeWidth={2} />
              </button>
            </>
          )}
        </div>
      )}

      <div
        className={`git-body files-file-body${editing ? ' files-file-body-editing' : ''}`}
        id="pane-file"
        role="region"
        aria-label={editing ? `Editing ${path}` : `Contents of ${path}`}
      >
        {busy && (
          <div className="thinking">
            <span className="spinner" /> reading file
          </div>
        )}
        {error && <div className="note note-error">{error}</div>}
        {saveError && <div className="note note-error">{saveError}</div>}
        {editing ? (
          <Editor path={path} value={draft} wrap={wrap} onChange={setDraft} editorRef={editorRef} />
        ) : (
          file &&
          !busy &&
          !error && (
            <>
              {file.binary ? (
                <Placeholder icon="fileBinary" line="Binary file" hint={formatSize(file.size)} />
              ) : raw === '' ? (
                <Placeholder icon="file" line="Empty file" hint="0 bytes" />
              ) : (
                <Code path={path} text={text} wrap={wrap} />
              )}
            </>
          )
        )}
      </div>
    </>
  );
}

function Placeholder({ icon, line, hint }: { icon: IconName; line: string; hint: string }): JSX.Element {
  return (
    <div className="files-placeholder">
      <Icon name={icon} size={26} strokeWidth={1.6} />
      <span className="files-placeholder-line">{line}</span>
      <span className="files-placeholder-hint">{hint}</span>
    </div>
  );
}

/** Lines past this render only when asked for: a long file must not stall a tap. */
const FIRST_LINES = 600;
const MORE_LINES = 1500;

function Code({ path, text, wrap }: { path: string; text: string; wrap: boolean }): JSX.Element {
  const plain = useMemo(() => plainLines(text), [text]);
  const [lines, setLines] = useState<CodeToken[][]>(plain);
  const [limit, setLimit] = useState(FIRST_LINES);

  useEffect(() => {
    setLimit(FIRST_LINES);
  }, [path, text]);

  // Plain text shows immediately; colour lands when the grammar has loaded.
  useEffect(() => {
    setLines(plain);
    const lang = languageForPath(path);
    if (!lang || text.length > MAX_HIGHLIGHT_BYTES) return;
    let cancelled = false;
    void highlightLines(text, lang).then((next) => {
      if (!cancelled) setLines(next);
    });
    return () => {
      cancelled = true;
    };
  }, [path, text, plain]);

  const visible = lines.slice(0, limit);

  return (
    <>
      <div className={`files-code${wrap ? ' files-code-wrap' : ''}`} style={gutterFor(lines.length)}>
        {visible.map((tokens, i) => (
          <CodeLine key={i} n={i + 1} tokens={tokens} />
        ))}
      </div>
      {lines.length > limit && (
        <button type="button" className="files-more" onClick={() => setLimit((n) => n + MORE_LINES)}>
          Show {Math.min(MORE_LINES, lines.length - limit).toLocaleString()} more lines
        </button>
      )}
    </>
  );
}

function CodeLine({ n, tokens }: { n: number; tokens: CodeToken[] }): JSX.Element {
  return (
    <div className="files-line">
      <span className="files-lineno" aria-hidden="true">
        {n}
      </span>
      <span className="files-linetext">
        {tokens.map((token, j) => (
          <span
            key={j}
            style={{
              color: token.color,
              ...(token.italic ? { fontStyle: 'italic' } : null),
              ...(token.bold ? { fontWeight: 600 } : null),
            }}
          >
            {token.text}
          </span>
        ))}
      </span>
    </div>
  );
}

/** Wide enough for the last line number, so both layers indent the same. */
function gutterFor(lines: number): CSSProperties {
  return { '--gutter': `${String(lines).length}ch` } as CSSProperties;
}

/**
 * Every keystroke repaints the whole file, so the editor caps well below the
 * viewer: past this it is a plain textarea, as it always was.
 */
const MAX_EDIT_BYTES = 40_000;

/**
 * A file being edited should look like the same file being read. The painted
 * lines sit under a textarea whose own text is transparent, and the caret,
 * selection and scrolling belong to the textarea alone.
 *
 * The two layers have to agree on everything that moves a glyph - font, line
 * height, tab size, padding, wrapping - or the caret drifts off the character
 * it is on; the CSS keeps those in one place. Painting is synchronous once the
 * grammar is in memory, because a frame of stale colour under a live caret
 * reads as a glitch.
 */
function Editor({
  path,
  value,
  wrap,
  onChange,
  editorRef,
}: {
  path: string;
  value: string;
  wrap: boolean;
  onChange: (text: string) => void;
  editorRef: RefObject<HTMLTextAreaElement | null>;
}): JSX.Element {
  const paintRef = useRef<HTMLDivElement>(null);
  const [grammar, setGrammar] = useState<LangId | null>(null);
  const lang = languageForPath(path);
  const painted = value.length <= MAX_EDIT_BYTES;

  useEffect(() => {
    if (!lang) return;
    let cancelled = false;
    void loadLanguage(lang).then(() => {
      if (!cancelled) setGrammar(lang);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const lines = useMemo(
    () =>
      (painted && grammar ? highlightLinesSync(value, grammar) : null) ??
      (painted ? plainLines(value) : []),
    [value, grammar, painted],
  );

  // The textarea is the only scroller; the paint layer is dragged along, which
  // keeps the line numbers sticky the way they are in the viewer.
  const sync = useCallback((): void => {
    const box = editorRef.current;
    const paint = paintRef.current;
    if (!box || !paint) return;
    paint.scrollTop = box.scrollTop;
    paint.scrollLeft = box.scrollLeft;
  }, [editorRef]);

  useLayoutEffect(sync, [sync, lines]);

  return (
    <div className={`files-edit${wrap ? ' files-edit-wrap' : ''}`} style={gutterFor(lines.length)}>
      {painted && (
        <div className="files-edit-paint" ref={paintRef} aria-hidden="true">
          {lines.map((tokens, i) => (
            <CodeLine key={i} n={i + 1} tokens={tokens} />
          ))}
        </div>
      )}
      <textarea
        ref={editorRef}
        className={`files-editor${painted ? '' : ' files-editor-plain'}`}
        value={value}
        wrap={wrap ? 'soft' : 'off'}
        onChange={(e) => onChange(e.target.value)}
        onScroll={sync}
        aria-label="File contents"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="enter"
      />
    </div>
  );
}

function Crumbs({ path, onGo }: { path: string; onGo: (path: string) => void }): JSX.Element {
  const parts = path ? path.split('/') : [];
  const row = useRef<HTMLDivElement>(null);

  // Deep paths overflow the header; the tail is the part that matters.
  useEffect(() => {
    const el = row.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [path]);

  return (
    <div className="files-crumb-row" ref={row}>
      <button
        type="button"
        className="files-crumb files-crumb-root"
        onClick={() => onGo('')}
        disabled={parts.length === 0}
        aria-label="Project root"
      >
        <Icon name="folder" size={15} strokeWidth={2} />
      </button>
      {parts.map((part, i) => (
        <span className="files-crumb-part" key={`${i}:${part}`}>
          <span className="files-crumb-sep">/</span>
          <button
            type="button"
            className="files-crumb"
            onClick={() => onGo(parts.slice(0, i + 1).join('/'))}
            disabled={i === parts.length - 1}
          >
            {part}
          </button>
        </span>
      ))}
    </div>
  );
}

type Look = { icon: IconName; tone: string };

const CODE_EXT = new Set([
  'c', 'cc', 'cjs', 'cpp', 'cs', 'cts', 'go', 'h', 'hpp', 'java', 'js', 'jsx', 'kt', 'lua', 'mjs',
  'mts', 'php', 'py', 'rb', 'rs', 'sh', 'sql', 'svelte', 'swift', 'ts', 'tsx', 'vue', 'zsh',
]);
const DATA_EXT = new Set([
  'cfg', 'conf', 'csv', 'env', 'ini', 'json', 'json5', 'jsonc', 'lock', 'plist', 'toml', 'tsv',
  'xml', 'yaml', 'yml',
]);
const DOC_EXT = new Set(['adoc', 'markdown', 'md', 'mdx', 'pdf', 'rst', 'txt']);
const STYLE_EXT = new Set(['css', 'less', 'sass', 'scss']);
const IMAGE_EXT = new Set(['avif', 'bmp', 'gif', 'heic', 'ico', 'jpeg', 'jpg', 'png', 'svg', 'webp']);
const BINARY_EXT = new Set([
  '7z', 'bin', 'dll', 'exe', 'gz', 'mp3', 'mp4', 'otf', 'so', 'tar', 'ttf', 'wasm', 'webm', 'woff',
  'woff2', 'zip',
]);

/** Icon and colour for a row, keyed off the extension. */
function lookOf(entry: FileEntry): Look {
  if (entry.kind === 'dir') return { icon: 'folder', tone: 'dir' };
  if (entry.kind === 'other') return { icon: 'file', tone: 'plain' };
  const name = entry.name.toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1) : '';
  if (name.startsWith('dockerfile') || name === 'makefile') return { icon: 'fileCode', tone: 'code' };
  if (CODE_EXT.has(ext)) return { icon: 'fileCode', tone: 'code' };
  if (STYLE_EXT.has(ext)) return { icon: 'fileCode', tone: 'style' };
  if (DATA_EXT.has(ext)) return { icon: 'fileData', tone: 'data' };
  if (IMAGE_EXT.has(ext)) return { icon: 'fileImage', tone: 'image' };
  if (BINARY_EXT.has(ext)) return { icon: 'fileBinary', tone: 'plain' };
  if (DOC_EXT.has(ext)) return { icon: 'file', tone: 'doc' };
  return { icon: 'file', tone: 'plain' };
}

function countLine(total: number, dirs: number, filtered: boolean): string {
  const files = total - dirs;
  const parts: string[] = [];
  if (dirs) parts.push(`${dirs} folder${dirs === 1 ? '' : 's'}`);
  if (files) parts.push(`${files} file${files === 1 ? '' : 's'}`);
  const body = parts.join(' · ') || 'nothing here';
  return filtered ? `${body} matching` : body;
}

function formatSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Coarse "how fresh is this" for the row, not a timestamp to read closely. */
function formatWhen(mtime: number): string {
  const secs = Math.max(0, (Date.now() - mtime) / 1000);
  if (secs < 90) return 'now';
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)}m`;
  const hours = mins / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = hours / 24;
  if (days < 7) return `${Math.round(days)}d`;
  const date = new Date(mtime);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? null : { year: '2-digit' }),
  });
}
